'use client';

import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { withDisplayName } from '../../lib/display-name';
import { SList, type SListProps } from '../SList';
import { SExpansionItem, type SExpansionItemProps } from '../SExpansionItem';
import { SListItem, type SListItemProps } from '../SListItem';

type AccordionOpenKeys = Partial<Record<NonNullable<SExpansionItemProps['depth']>, string>>;
type SelectedKeys = Partial<Record<NonNullable<SListItemProps['depth']>, string>>;

export type SExpansionListProps = SListProps;

function isExpansionItemElement(node: ReactNode): node is ReactElement<SExpansionItemProps> {
  return isValidElement(node) && node.type === SExpansionItem;
}

function isListItemElement(node: ReactNode): node is ReactElement<SListItemProps> {
  return isValidElement(node) && node.type === SListItem;
}

function getInitialOpenKeys(children: ReactNode): AccordionOpenKeys {
  const openKeys: AccordionOpenKeys = {};
  Children.forEach(children, (child, index) => {
    if (!isExpansionItemElement(child)) return;
    const depth = child.props.depth ?? 1;
    if (openKeys[depth] != null) return;
    if (child.props.expanded === true || child.props.defaultExpanded === true) {
      openKeys[depth] = String(child.key ?? index);
    }
  });
  return openKeys;
}

function getInitialSelectedKeys(children: ReactNode): SelectedKeys {
  const selectedKeys: SelectedKeys = {};
  Children.forEach(children, (child, index) => {
    if (!isListItemElement(child) || !child.props.selected) return;
    const depth = child.props.depth ?? 1;
    if (selectedKeys[depth] == null) selectedKeys[depth] = String(child.key ?? index);
  });
  return selectedKeys;
}

const SExpansionListImpl = forwardRef<HTMLDivElement, SExpansionListProps>(function SExpansionList(
  { children, ...rest },
  ref,
) {
  const [openKeys, setOpenKeys] = useState<AccordionOpenKeys>(() => getInitialOpenKeys(children));
  const [selectedKeys, setSelectedKeys] = useState<SelectedKeys>(() =>
    getInitialSelectedKeys(children),
  );

  const listChildren = Children.map(children, (child, index) => {
    if (isListItemElement(child)) {
      const depth = child.props.depth ?? 1;
      const itemKey = String(child.key ?? index);
      return cloneElement(child, {
        selected: selectedKeys[depth] === itemKey,
        onClick: event => {
          setSelectedKeys(current => ({ ...current, [depth]: itemKey }));
          child.props.onClick?.(event);
        },
      });
    }

    if (!isExpansionItemElement(child)) return child;

    const depth = child.props.depth ?? 1;
    const itemKey = String(child.key ?? index);
    return cloneElement(child, {
      expanded: openKeys[depth] === itemKey,
      selected: undefined,
      onToggle: (next, event) => {
        setOpenKeys(current => ({
          ...current,
          [depth]: next ? itemKey : current[depth] === itemKey ? undefined : current[depth],
        }));
        child.props.onToggle?.(next, event);
      },
    });
  });

  return (
    <SList ref={ref} {...rest}>
      {listChildren}
    </SList>
  );
});

export const SExpansionList = withDisplayName(SExpansionListImpl, 'SExpansionList');
